// NPM
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import moment from "moment";

// ANT COMPONENTS
import { ArrowLeftOutlined } from "@ant-design/icons";
import { Empty, Avatar, Row, Col, Input, Spin } from "antd";
import { AlertBox, BasicModal, Button } from "@life-line-lab/components-react";

// STYLE
import "./customer-location-details.scss";
import "./../customer-management.scss";

// TYPES
import { CustomerLocationFormType } from "Types/customer_location.types";
import { CustomerCompanyFormInputType } from "Types/customer-form.types";

// JSON LIST
import countryList from "../../../data/country.json";
import { ModalType } from "Types/common.types";
import { useRecoilState } from "recoil";
import {
  customerLocationAtom,
  CustomerLocationAtomType,
} from "Recoil/customer_location.atom";

interface CustomerLocationDetailsComponentProps {
  onBack: () => void;
  onEdit?: () => void;
  onFetchCustomerLocation: () => void;
  onDeleteLocation: (orgID: string, tempDeletedReason: string) => void;
  onRestoreLocation: (orgID: string) => void;
  locationDetails: Partial<CustomerLocationFormType>;
  customerDetails: Partial<CustomerCompanyFormInputType>;
  isReadonly: boolean;
  isLoading?: boolean;
}

const CustomerLocationDetailsComponent: React.FC<
  CustomerLocationDetailsComponentProps
> = ({
  onBack,
  onEdit,
  onFetchCustomerLocation,
  onDeleteLocation,
  onRestoreLocation,
  locationDetails,
  customerDetails,
  isReadonly,
  isLoading = false,
}) => {
  const { t } = useTranslation();
  const history = useHistory();

  const initialModal: ModalType<Partial<CustomerLocationFormType>> = {
    open: false,
    type: "restore",
    record: {},
  };
  const initialError = {};
  const initialInput = { reason: "" };

  const [modal, setModal] =
    useState<ModalType<Partial<CustomerLocationFormType>>>(initialModal);
  const [input, setInput] = useState<any>(initialInput);
  const [error, setError] = useState<any>(initialError);
  const [customerLocationStatus, setCustomerLocationStatus] =
    useRecoilState<CustomerLocationAtomType>(customerLocationAtom);

  useEffect(() => {
    if (customerLocationStatus.delete === "success") {
      resetModalState();
      onFetchCustomerLocation();
    }
    if (customerLocationStatus.restore === "success") {
      console.log("Location restored successfully");
      resetModalState();
      setCustomerLocationStatus((prevStatus) => ({
        ...prevStatus,
        restore: "idle",
      }));
      setTimeout(() => {
        history.push("/customer-management/location");
      }, 250);
    }
  }, [customerLocationStatus]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setError(initialError);
    setInput({ ...input, [e.target.name]: e.target.value });
  };

  const handleEdit = () => {
    if (onEdit) {
      onEdit();
    }
  };

  const confirmRestore = () => {
    onRestoreLocation(locationDetails._id || "");
  };

  const confirmDelete = () => {
    if (input.reason.length === 0) {
      setError({
        ...error,
        reason: t("error.required", { field: t(`label.reason`) }),
      });
    } else {
      onDeleteLocation(locationDetails._id || "", input.reason);
    }
  };

  const resetModalState = () => {
    setModal(initialModal);
    setInput(initialInput);
    setError(initialError);
  };

  const ActionButtons = () => {
    if (isReadonly) {
      return null;
    } else if (
      !locationDetails.tempDeleteInfo?.tempDelete &&
      locationDetails.primary != true
    ) {
      return (
        <React.Fragment>
          <Button
            type="primary"
            className="mr-10"
            label={t("action.edit")}
            onClick={() => {
              handleEdit();
            }}
          />
          <Button
            type="danger"
            label={t("action.delete")}
            onClick={() => {
              setModal({
                ...modal,
                open: true,
                type: "delete",
                record: locationDetails,
              });
            }}
          />
        </React.Fragment>
      );
    } else if (
      !locationDetails.tempDeleteInfo?.tempDelete &&
      locationDetails.primary == true
    ) {
      return (
        <React.Fragment>
          <Button
            type="primary"
            className="mr-10"
            label={t("action.edit")}
            onClick={() => {
              handleEdit();
            }}
          />
        </React.Fragment>
      );
    } else {
      return (
        <Button
          type="primary"
          label={t("action.restore")}
          onClick={() => {
            setModal({
              ...modal,
              open: true,
              type: "restore",
              record: locationDetails,
            });
          }}
        />
      );
    }
  };

  return (
    <div className="p-20 my-20 customer-location-details-container">
      {isLoading ? (
        <div className="loading-container">
          <Spin size="large" />
        </div>
      ) : (
        <>
          <div className="main-header">
            <span onClick={onBack} className="cursor-pointer back-action">
              <ArrowLeftOutlined />
              <span className="ml-5">{t("txt.back")}</span>
            </span>
            <span className="center header-title">
              {t("title.customerLocationDetails").toLocaleUpperCase()}
              {`  |  ID: ${locationDetails.locationCode}`}
            </span>
          </div>
          <Avatar
            className="center my-40"
            size={128}
            src={
              customerDetails.file?.file_url || Empty.PRESENTED_IMAGE_DEFAULT
            }
          />
          <Row gutter={[15, 15]} className="customer-details-container">
            <Col xs={24} sm={24} md={12} xl={6}>
              <div className="bold-label">
                {t("label.customerName").toUpperCase()}
              </div>
              <div className="fs-13">
                {locationDetails.customerName || <span>&#11835;</span>}
              </div>
            </Col>
            <Col xs={24} sm={24} md={12} xl={6}>
              <div className="bold-label">
                {t("label.locationName").toLocaleUpperCase()}
              </div>
              <div className="fs-13">
                {locationDetails.primary == true
                  ? "Primary Location"
                  : locationDetails.customerLocationName || (
                      <span>&#11835;</span>
                    )}
              </div>
            </Col>
            <Col xs={24} sm={24} md={12} xl={6}>
              <div className="bold-label">
                {t("label.contactPerson").toUpperCase()}
              </div>
              <div className="fs-13">{locationDetails.contactPerson}</div>
            </Col>
            <Col xs={24} sm={24} md={12} xl={6}>
              <div className="bold-label">
                {t("label.emailAddress").toUpperCase()}
              </div>
              <div className="fs-13">
                {locationDetails.emailAddress ||
                  customerDetails?.emailAddress ||
                  "N/A"}
              </div>
            </Col>
            <Col xs={24} sm={24} md={12} xl={6}>
              <div className="bold-label">
                {t("label.registeredAddress").toUpperCase()}
              </div>
              <div className="address-line-break fs-13">
                {locationDetails.street1 + ", "}
                {locationDetails.street2 ? locationDetails.street2 + ", " : ""}
                {locationDetails.city + ", "}
                {locationDetails.state + ", "}
                {locationDetails.postcode + ", "}
                {countryList.find((country) => {
                  return country.Code === locationDetails.country;
                })?.Name || ""}
              </div>
            </Col>
            <Col xs={24} sm={24} md={12} xl={6}>
              <div className="bold-label">
                {t("label.contactNo").toUpperCase()}
              </div>
              <div className="fs-13">
                {`+${locationDetails?.contactNo?.dialCode} 
                            ${locationDetails?.contactNo?.number}`}
              </div>
            </Col>
            {locationDetails.tempDeleteInfo?.tempDelete ? (
              <Col xs={24} sm={24} md={12} xl={6}>
                <div className="bold-label">
                  {t("label.deleteReason").toUpperCase()}
                </div>
                <div className="fs-13">
                  {locationDetails.tempDeleteInfo.tempDeletedReason}
                </div>
              </Col>
            ) : (
              <Col xs={24} sm={24} md={24} xl={12}>
                <div className="bold-label">
                  {t("label.operationalHours").toLocaleUpperCase()}
                </div>
                {locationDetails?.workingHours?.map((hour, index) => {
                  return (
                    <Row key={index}>
                      <Col span={24} xxl={8}>
                        <div className="label">
                          {hour?.day?.charAt(0) +
                            hour?.day?.substring(1).toLowerCase()}
                          :
                        </div>
                      </Col>
                      <Col span={24} xxl={16}>
                        {moment(hour?.openingTime).format("hh:mm a")} -{" "}
                        {moment(hour?.closingTime).format("hh:mm a")}
                      </Col>
                    </Row>
                  );
                })}
              </Col>
            )}
            <Col xs={24} sm={24} md={24} xl={12}>
              <div className="bold-label">
                {t("label.handlingInstructions").toUpperCase()}
              </div>
              <div className="fs-13">
                {locationDetails.handlingInstructions}
              </div>
            </Col>
          </Row>
          <div className="right mt-50">
            <ActionButtons />
            {/* {isReadonly ? null : !locationDetails.tempDeleteInfo?.tempDelete ? (
          <React.Fragment>
            <Button
              type="primary"
              className="mr-10"
              label={t("action.edit")}
              onClick={() => {
                handleEdit();
              }}
            />
            <Button
              type="danger"
              label={t("action.delete")}
              onClick={() => {
                setModal({
                  ...modal,
                  open: true,
                  type: "delete",
                  record: locationDetails,
                });
              }}
            />
          </React.Fragment>
        ) : (
          <Button
            type="primary"
            label={t("action.restore")}
            onClick={() => {
              setModal({
                ...modal,
                open: true,
                type: "restore",
                record: locationDetails,
              });
            }}
          />
        )} */}
          </div>
          <BasicModal
            isOpen={modal.open}
            isClosable={true}
            isMaskClosable={false}
            onClose={() => {
              resetModalState();
            }}
          >
            {modal.type === "restore" ? (
              <div className="customer-restore-modal">
                <div className="modal-title">
                  {t("label.restoreCustomerLocation").toUpperCase()}
                </div>
                <div>{t("txt.areYouSureRestoreLocation")}</div>
                <div>
                  <b>{t("txt.customer").toUpperCase()}: </b>
                  {modal.record.customerName || <span>&#11835;</span>}
                </div>
                <Row>
                  <Col xs={24} sm={12}>
                    <b>{t("txt.location").toUpperCase()}: </b>
                    {modal.record.customerLocationName || <span>&#11835;</span>}
                  </Col>
                  <Col xs={24} sm={12}>
                    <b>{t("txt.locationID").toUpperCase()}: </b>
                    {modal.record.locationCode || <span>&#11835;</span>}
                  </Col>
                </Row>
                <div className="mt-30 right">
                  <Button
                    type="success"
                    label={t("action.confirm")}
                    onClick={() => {
                      confirmRestore();
                    }}
                  />
                  <Button
                    type="success"
                    label={t("action.cancel")}
                    className="btn--ghost"
                    onClick={() => {
                      resetModalState();
                    }}
                  />
                </div>
              </div>
            ) : (
              <div className="customer-delete-modal">
                <div className="modal-title">
                  {t("label.deleteCustomerLocation").toUpperCase()}
                </div>
                <div>{t("txt.areYouSureDeleteLocation")}</div>
                <div>
                  <b>{t("txt.customer").toUpperCase()}: </b>
                  {modal.record.customerName || <span>&#11835;</span>}
                </div>
                <Row>
                  <Col xs={24} sm={12}>
                    <b>{t("txt.location").toUpperCase()}: </b>
                    {modal.record.customerLocationName || <span>&#11835;</span>}
                  </Col>
                  <Col xs={24} sm={12}>
                    <b>{t("txt.locationID").toUpperCase()}: </b>
                    {modal.record.locationCode || <span>&#11835;</span>}
                  </Col>
                </Row>
                <div>
                  <label className="inp-label inp-label--required">
                    {t("label.enterReason")}
                  </label>
                  <Input.TextArea
                    id="message"
                    size="middle"
                    rows={3}
                    name="reason"
                    value={input.reason}
                    onChange={handleChange}
                    className="mt-5"
                    required={true}
                    placeholder={t("label.enterHere")}
                  />
                  <AlertBox text={error.reason} />
                </div>
                <div className="mt-30 right">
                  <Button
                    type="danger"
                    label={t("action.delete")}
                    onClick={() => {
                      confirmDelete();
                    }}
                  />
                  <Button
                    type="danger"
                    label={t("action.cancel")}
                    className="btn--ghost"
                    onClick={() => {
                      resetModalState();
                    }}
                  />
                </div>
              </div>
            )}
          </BasicModal>
        </>
      )}
    </div>
  );
};

export default CustomerLocationDetailsComponent;
